/**
 * StateContractEnforcer — enforces stateContract-based access control.
 *
 * Rules:
 * - @{localVar}: always allowed (block-scoped)
 * - state.json keys: allowed (own state)
 * - outputSymbols paths: allowed (declared cross-block writes)
 * - _system.*: READ ONLY (write throws SecurityError)
 * - Everything else: throw SecurityError on write
 */

import type { StateContract } from "@cogo/types-v2"
import { SecurityError } from "@cogo/types-v2"
import { AuditLog, type AuditEntry } from "./audit-log"

export class StateContractEnforcer {
  private localKeys: Set<string>
  private ownStateKeys: Set<string>
  private writablePaths: Set<string>
  private readablePaths: Set<string>
  private auditLog: AuditLog

  constructor(
    contract: StateContract,
    ownStateKeys: string[],
  ) {
    this.auditLog = new AuditLog()

    // @{} scope — localState keys
    this.localKeys = new Set(Object.keys(contract.localState))

    // Own state keys from state.json
    this.ownStateKeys = new Set(ownStateKeys)

    // Writable paths from outputSymbols
    this.writablePaths = new Set(
      contract.outputSymbols.map((s) => this.normalizePath(s.path)),
    )

    // Readable paths from inputSymbols
    this.readablePaths = new Set(
      contract.inputSymbols.map((s) => this.normalizePath(s.path)),
    )
  }

  /** Check if a write to the given path is allowed. Throws SecurityError if not. */
  checkWrite(path: string): void {
    // @{local} — always allowed
    if (path.startsWith("@")) return

    // _system.* — read-only
    if (path.startsWith("_system")) {
      this.auditLog.logUnauthorized("write", path, "system_readonly")
      throw new SecurityError(
        `Write denied: _system.* is read-only (attempted: ${path})`,
      )
    }

    // Own state keys — allowed
    const rootKey = path.split(".")[0]
    if (this.ownStateKeys.has(rootKey)) return

    // outputSymbols paths — allowed (exact or parent match)
    if (this.isPathAllowed(path, this.writablePaths)) return

    // Everything else — denied
    this.auditLog.logUnauthorized("write", path, "not_in_contract")
    throw new SecurityError(
      `Write denied: path "${path}" not declared in stateContract (localState, state.json, or outputSymbols)`,
    )
  }

  /** Check if a read from the given path is allowed for cross-block access. */
  checkCrossBlockRead(path: string): void {
    // @{local} — always allowed
    if (path.startsWith("@")) return

    // _system.* — read allowed
    if (path.startsWith("_system")) return

    // Own state — always readable
    const rootKey = path.split(".")[0]
    if (this.ownStateKeys.has(rootKey)) return

    // inputSymbols — allowed
    if (this.isPathAllowed(path, this.readablePaths)) return

    // outputSymbols — also readable (you can read what you write)
    if (this.isPathAllowed(path, this.writablePaths)) return

    this.auditLog.logUnauthorized("read", path, "not_in_contract")
    throw new SecurityError(
      `Read denied: path "${path}" not declared in stateContract inputSymbols`,
    )
  }

  /** Get all audit log entries. */
  getAuditEntries(): AuditEntry[] {
    return this.auditLog.getEntries()
  }

  /** Clear audit log. */
  clearAuditLog(): void {
    this.auditLog.clear()
  }

  /** Normalize #{...} path notation to plain path. */
  private normalizePath(path: string): string {
    if (path.startsWith("#{") && path.endsWith("}")) {
      return path.slice(2, -1).trim()
    }
    return path.trim()
  }

  /**
   * Check if a path matches any allowed path:
   * - Exact match: "cart.items" matches "cart.items"
   * - Sub-path: "cart.items.0.name" matches if "cart.items" is allowed
   * - Parent: "cart" matches if "cart.items" is allowed (writing parent includes children)
   */
  private isPathAllowed(path: string, allowedPaths: Set<string>): boolean {
    if (allowedPaths.has(path)) return true
    for (const allowed of allowedPaths) {
      if (path.startsWith(allowed + ".")) return true
      if (allowed.startsWith(path + ".")) return true
    }
    return false
  }
}
